import { Box, Button, Stack, Typography } from '@mui/material';
import { Download } from 'lucide-react';
import { useCallback, useDeferredValue, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import SummaryStatsRow from '~/components/Statistics/SummaryStatsRow';
import { formatDisplayIntensity } from '~/domain/dicom/intensityUnits';
import { getDICOMDate, getDICOMTime } from '~/domain/dicom/utils';
import { midpointMm } from '~/domain/geometry/patientGeometry';
import { ProfileLine } from '~/domain/intensity/lineProfile';
import { useImageForSeries } from '~/hooks/image/useImageForSeries';
import type { LineProfileController } from '~/hooks/useLineProfileController';
import {
    PROFILE_HOVER_TOLERANCE_MM,
    profileHoverDistanceState,
    profileHoverState,
    withoutProfileHover,
} from '~/state/atoms/intensityToolAtoms';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import {
    createCSVContent,
    downloadCreatedFile,
} from '~/utils/io/files/handleFileExport';
import { formatDistanceCm } from '~/utils/measurementFormat';
import {
    SUMMARY_STAT_LABEL_IDS,
    type SummaryStatKey,
} from '~/utils/summaryStatLabels';
import {
    ProfileDataset,
    ProfileOmissions,
} from '~/domain/intensity/lineProfileDatasets';
import {
    LINE_PROFILE_CSV_HEADER,
    buildLineProfileCsvRows,
} from './lineProfileExport';
import { cmToMm, mmToCm } from '~/utils/units';
import AnalysisHeader from './AnalysisHeader';
import LineProfileChart from './LineProfileChart';
import { toCurves } from './lineProfileCurves';

function formatDatasetValue(dataset: ProfileDataset, value: number): string {
    return formatDisplayIntensity(dataset.toDisplay(value), dataset.unit);
}

/**
 * min/max/mean/SD for one dataset in its display unit. The name (PHI → via
 * {@link SensitiveInfoTypography}) and swatch show only when curves compare.
 */
function DatasetStats({
    dataset,
    showName,
}: {
    dataset: ProfileDataset;
    showName: boolean;
}) {
    const { stats } = dataset;
    const description = dataset.label.trim();
    // Units always shown for clinical values (ui_specs).
    const format = (value: number): string =>
        formatDisplayIntensity(dataset.toDisplay(value), '');
    return (
        <Box>
            {showName && (
                <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ mb: 0.25 }}
                >
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '2px',
                            backgroundColor: dataset.color,
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 600,
                            color: 'text.secondary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {description ? (
                            <SensitiveInfoTypography>
                                {description}
                            </SensitiveInfoTypography>
                        ) : (
                            dataset.modality
                        )}
                    </Typography>
                    {description && (
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.disabled', flexShrink: 0 }}
                        >
                            {dataset.modality}
                        </Typography>
                    )}
                </Stack>
            )}
            <SummaryStatsRow
                stats={stats}
                format={format}
                unit={dataset.unit}
            />
        </Box>
    );
}

/**
 * Lists how many loaded datasets are missing from the chart, one line per
 * reason. Without this, a chart with fewer curves looks like a chart of every
 * dataset there is.
 */
function OmittedDatasetsNote({ omitted }: { omitted: ProfileOmissions }) {
    const intl = useIntl();
    const reasons: { id: string; count: number }[] = [
        {
            id: 'lineProfile.omittedOtherFrame',
            count: omitted.otherFrameOfReference,
        },
        {
            id: 'lineProfile.omittedUnsampleable',
            count: omitted.unsampleable,
        },
    ];
    return (
        <>
            {reasons
                .filter((reason) => reason.count > 0)
                .map((reason) => (
                    <Typography
                        key={reason.id}
                        variant="caption"
                        sx={{
                            display: 'block',
                            mt: 0.5,
                            color: 'text.disabled',
                        }}
                    >
                        {intl.formatMessage(
                            { id: reason.id },
                            { count: reason.count }
                        )}
                    </Typography>
                ))}
        </>
    );
}

/**
 * The selected profile's analysis. Pointing at its chart publishes the
 * distance in `profileHoverState`, withdrawn on leave and on unmount.
 *
 * A profile still being placed, with no `id`, is analysed as it is drawn: the
 * chart follows the cursor, and there is no go-to, close, chart pointing or
 * export, as no profile is placed yet. It says when the cursor is off the
 * image, where the line cannot end.
 */
export default function LineProfileDetail({
    profile,
    comparisonForProfile,
    goTo,
}: {
    profile: ProfileLine & { readonly id?: string };
    /**
     * Taken as a prop rather than from the line profile controller, which
     * also subscribes to the viewport views: a viewport layout change would
     * then redraw these charts for nothing.
     */
    comparisonForProfile: LineProfileController['comparisonForProfile'];
    goTo: (id: string) => void;
}) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const profileId = profile.id;
    const preview = profileId === undefined;
    const setHover = useSetRecoilState(profileHoverState);
    // '' is no profile's id, so a preview reads no hover.
    const hoveredMm =
        useRecoilValue(profileHoverDistanceState(profileId ?? '')) ?? undefined;
    const ownGrid = useImageForSeries()(profile.plane.seriesInstanceUid)?.grid;
    const endOffImage =
        preview && ownGrid?.includesPoint(profile.endMm) === false;

    // The chart follows an endpoint drag, and the cursor while the line is
    // being placed; sampled at a lower priority, so re-reading every dataset
    // never holds up the line on the image.
    const sampled = useDeferredValue(profile);
    const { unitGroups, omitted } = useMemo(
        () => comparisonForProfile(sampled, { logOmissions: !preview }),
        [sampled, comparisonForProfile, preview]
    );
    // Memoized so the memoized charts re-lay out only for new samples.
    const curveGroups = useMemo(
        () => unitGroups.map((group) => toCurves(group)),
        [unitGroups]
    );
    const datasets = unitGroups.flatMap((group) => group.datasets);
    const own = datasets.find((dataset) => dataset.isOwn);
    // The title and the footer read the image the line was drawn on; any
    // other curve is listed by name, or its figures would be shown nowhere.
    const showNames = datasets.some((dataset) => !dataset.isOwn);

    // Stacked unit charts share one distance axis, so they share an x-max;
    // samples are distance-ordered, so the last one is the max.
    const xMaxCm = useMemo(() => {
        let maxMm = 0;
        for (const group of unitGroups) {
            for (const dataset of group.datasets) {
                const last = dataset.samples[dataset.samples.length - 1];
                if (last && last.distanceMm > maxMm) {
                    maxMm = last.distanceMm;
                }
            }
        }
        return maxMm > 0 ? mmToCm(maxMm) : undefined;
    }, [unitGroups]);
    // Compact stacked charts so the common image+dose pair fits unscrolled.
    const chartHeight = unitGroups.length > 1 ? 130 : 170;

    const handleExport = useCallback(() => {
        const rows = buildLineProfileCsvRows(unitGroups, showSensitiveInfo);
        downloadCreatedFile(
            createCSVContent(LINE_PROFILE_CSV_HEADER, rows),
            `line_profile_${getDICOMDate()}_${getDICOMTime()}.csv`
        );
    }, [unitGroups, showSensitiveInfo]);

    // Writes only when the snapped distance changes, and clears only this
    // profile's point, so a move within one sample redraws nothing.
    const onHoverDistance = useCallback(
        (distanceCm: number | undefined) => {
            if (profileId === undefined) {
                return;
            }
            setHover((current) => {
                if (distanceCm === undefined) {
                    return withoutProfileHover(profileId)(current);
                }
                const distanceMm = cmToMm(distanceCm);
                return current?.id === profileId &&
                    current.distanceMm === distanceMm
                    ? current
                    : { id: profileId, distanceMm };
            });
        },
        [setHover, profileId]
    );
    useEffect(
        () => () => {
            if (profileId !== undefined) {
                setHover(withoutProfileHover(profileId));
            }
        },
        [setHover, profileId]
    );

    const ownFigure = (stat: SummaryStatKey) =>
        own &&
        `${intl.formatMessage({ id: SUMMARY_STAT_LABEL_IDS[stat] })} ${formatDatasetValue(own, own.stats[stat])}`;
    // A gap, or a distance only another dataset was sampled at, has no value.
    const hovered = own?.samples.find(
        (sample) =>
            hoveredMm !== undefined &&
            Math.abs(sample.distanceMm - hoveredMm) < PROFILE_HOVER_TOLERANCE_MM
    );
    const header = (
        <AnalysisHeader
            id={profileId}
            plane={profile.plane}
            anchorMm={midpointMm(profile.startMm, profile.endMm)}
            titleId="measure.mode.profile"
            readout={
                hoveredMm === undefined
                    ? ownFigure('mean')
                    : `${formatDistanceCm(hoveredMm)} · ${
                          own && hovered
                              ? formatDatasetValue(own, hovered.value)
                              : '—'
                      }`
            }
            goTo={goTo}
        />
    );

    const offImageNote = endOffImage && (
        <Typography
            variant="caption"
            data-testid="line-profile-end-off-image"
            sx={{ display: 'block', color: 'warning.main' }}
        >
            {intl.formatMessage({ id: 'lineProfile.preview.endOffImage' })}
        </Typography>
    );

    if (unitGroups.length === 0) {
        return (
            <>
                {header}
                {offImageNote}
                <Typography
                    variant="caption"
                    sx={{ display: 'block' }}
                    color="text.secondary"
                >
                    {intl.formatMessage({ id: 'lineProfile.noData' })}
                </Typography>
                <OmittedDatasetsNote omitted={omitted} />
            </>
        );
    }

    return (
        <>
            {header}
            {offImageNote}
            {unitGroups.map((group, index) => (
                <LineProfileChart
                    key={group.datasets[0].key}
                    curves={curveGroups[index]}
                    unit={group.unit}
                    height={chartHeight}
                    xMaxCm={xMaxCm}
                    showDistanceAxis={index === unitGroups.length - 1}
                    onHoverDistance={preview ? undefined : onHoverDistance}
                />
            ))}
            <Stack
                direction="row"
                justifyContent="space-between"
                data-testid="measure-analysis-footer"
                sx={{
                    flexWrap: 'wrap',
                    columnGap: 1.5,
                    rowGap: 0.25,
                    color: 'text.secondary',
                    fontSize: 12,
                    fontVariantNumeric: 'tabular-nums',
                    '& > span': { whiteSpace: 'nowrap' },
                }}
            >
                <span>{ownFigure('min')}</span>
                <span>{ownFigure('max')}</span>
                <span>{ownFigure('std')}</span>
            </Stack>
            {showNames && (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {datasets.map((dataset) => (
                        <DatasetStats
                            key={dataset.key}
                            dataset={dataset}
                            showName
                        />
                    ))}
                </Stack>
            )}
            {/* Discloses that, unlike cursor/DVH, no display dose scale is
                applied. */}
            {datasets.some((dataset) => dataset.modality === 'RTDOSE') && (
                <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 0.5, color: 'text.disabled' }}
                >
                    {intl.formatMessage({ id: 'lineProfile.doseUnscaled' })}
                </Typography>
            )}
            <OmittedDatasetsNote omitted={omitted} />
            {!preview && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 0.5,
                    }}
                >
                    <Button
                        size="small"
                        color="inherit"
                        startIcon={<Download size={14} />}
                        onClick={handleExport}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                        {intl.formatMessage({ id: 'lineProfile.export' })}
                    </Button>
                </Box>
            )}
        </>
    );
}
